/**
 * Counter Display Page
 * Shows all orders with items ready to be gathered for takeaway
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  RefreshCw, 
  LogOut, 
  Sun, 
  Moon,
  Package,
  ShoppingBag,
  UtensilsCrossed,
  Volume2,
  VolumeX,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import KDSOrderCard from '@/components/kds/KDSOrderCard';
import { api } from '@/utils/apiClient';

export default function CounterDisplayPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deviceId = searchParams.get('device') || 'KDS-CTR-01';
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDark, setIsDark] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [filter, setFilter] = useState('all'); // all, takeaway, dine-in
  const [session, setSession] = useState(null);
  
  const audioRef = useRef(null);
  const lastOrderCountRef = useRef(0);

  // Load session and theme
  useEffect(() => {
    const savedSession = localStorage.getItem('kds-session');
    if (savedSession) {
      setSession(JSON.parse(savedSession));
    }
    
    const savedTheme = localStorage.getItem('kds-dark-mode');
    if (savedTheme === 'true') {
      setIsDark(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleTheme = () => {
    setIsDark(!isDark);
    if (!isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('kds-dark-mode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('kds-dark-mode', 'false');
    }
  };

  // Play notification sound
  const playChime = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  }, [soundEnabled]);

  // Fetch counter orders
  const fetchOrders = useCallback(async () => {
    try {
      const response = await api.get('/api/kds/counter');
      const newOrders = response.data.orders || [];
      
      // Check if new items arrived
      const newReadyCount = newOrders.reduce((sum, o) => sum + o.ready_count, 0);
      if (newReadyCount > lastOrderCountRef.current && lastOrderCountRef.current > 0) {
        playChime();
      }
      lastOrderCountRef.current = newReadyCount;
      
      setOrders(newOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [playChime]);

  // Initial fetch and polling
  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // Heartbeat
  useEffect(() => {
    const heartbeat = async () => {
      try {
        await api.post('/api/devices/heartbeat', { device_id: deviceId });
      } catch (error) {
        console.error('Heartbeat error:', error);
      }
    };
    
    heartbeat();
    const interval = setInterval(heartbeat, 60000);
    return () => clearInterval(interval);
  }, [deviceId]);

  // Handle item gather
  const handleItemGather = async (orderId, itemIndex) => {
    try {
      await api.post('/api/kds/item/gather', {
        order_id: orderId,
        item_index: itemIndex,
        staff_id: session?.staff_id,
        device_id: deviceId
      });
      
      fetchOrders();
    } catch (error) {
      console.error('Error gathering item:', error);
      toast.error('Failed to update item');
    }
  };

  // Handle item ungather
  const handleItemUngather = async (orderId, itemIndex) => {
    try {
      await api.post('/api/kds/item/ungather', {
        order_id: orderId,
        item_index: itemIndex,
        staff_id: session?.staff_id,
        device_id: deviceId
      });
      
      fetchOrders();
    } catch (error) {
      console.error('Error ungathering item:', error);
      toast.error('Failed to update item');
    }
  };

  // Handle order complete
  const handleOrderComplete = async (orderId) => {
    try {
      const response = await api.post('/api/kds/order/complete', {
        order_id: orderId,
        staff_id: session?.staff_id,
        device_id: deviceId
      });
      
      toast.success(`Order complete! (${response.data.completion_time_minutes}min)`);
      fetchOrders();
    } catch (error) {
      console.error('Error completing order:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete order');
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      if (session?.session_id) {
        await api.post('/api/device-sessions/logout', {
          device_id: deviceId,
          session_id: session.session_id
        });
      }
      
      localStorage.removeItem('kds-session');
      localStorage.removeItem('kds-staff');
      localStorage.removeItem('kds-device');
      navigate('/kds/login?device=' + deviceId);
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/kds/login?device=' + deviceId);
    }
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    return order.order_type === filter;
  });

  // Count stats
  const takeawayCount = orders.filter(o => o.order_type === 'takeaway').length;
  const dineInCount = orders.filter(o => o.order_type === 'dine-in').length;
  const totalReady = orders.reduce((sum, o) => sum + o.ready_count, 0);

  return (
    <div className={`min-h-screen ${isDark ? 'dark bg-gray-900' : 'bg-gray-100'}`}>
      {/* Hidden audio element for chime */}
      <audio ref={audioRef} src="/sounds/chime.mp3" preload="auto" />
      
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900 dark:text-white">Counter Display</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">{deviceId}</p>
              </div>
            </div>
            
            {/* Filter Tabs */}
            <div className="hidden md:flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  filter === 'all' 
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow' 
                    : 'text-gray-600 dark:text-gray-300'
                }`}
              >
                All ({orders.length})
              </button>
              <button
                onClick={() => setFilter('takeaway')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  filter === 'takeaway' 
                    ? 'bg-orange-500 text-white shadow' 
                    : 'text-gray-600 dark:text-gray-300'
                }`}
              >
                <ShoppingBag className="w-4 h-4 inline mr-1" />
                Takeaway ({takeawayCount})
              </button>
              <button
                onClick={() => setFilter('dine-in')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  filter === 'dine-in' 
                    ? 'bg-blue-500 text-white shadow' 
                    : 'text-gray-600 dark:text-gray-300'
                }`}
              >
                <UtensilsCrossed className="w-4 h-4 inline mr-1" />
                Dine-in ({dineInCount})
              </button>
            </div>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {/* Sound Toggle */}
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`p-2 rounded-lg transition-colors ${
                soundEnabled 
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
              }`}
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-yellow-500" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>
            
            {/* Refresh */}
            <button
              onClick={fetchOrders}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-300 ${loading ? 'animate-spin' : ''}`} />
            </button>
            
            {/* Logout */}
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-green-500" />
            <span className="text-gray-600 dark:text-gray-300">
              <strong className="text-green-600 dark:text-green-400">{totalReady}</strong> items ready
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-orange-500" />
            <span className="text-gray-600 dark:text-gray-300">
              <strong className="text-orange-600 dark:text-orange-400">{takeawayCount}</strong> takeaway
            </span>
          </div>
          <div className="flex items-center gap-2">
            <UtensilsCrossed className="w-4 h-4 text-blue-500" />
            <span className="text-gray-600 dark:text-gray-300">
              <strong className="text-blue-600 dark:text-blue-400">{dineInCount}</strong> dine-in
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        {loading && orders.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Package className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-xl font-medium text-gray-600 dark:text-gray-400">No orders ready</h3>
            <p className="text-gray-400 dark:text-gray-500 mt-1">Orders will appear here when items are marked ready</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <AnimatePresence mode="popLayout">
              {filteredOrders.map((order) => (
                <KDSOrderCard
                  key={order.order_id}
                  order={order}
                  showGatherCheckbox={true}
                  showReadyButton={false}
                  onItemGather={handleItemGather}
                  onItemUngather={handleItemUngather}
                  onOrderComplete={handleOrderComplete}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>
    </div>
  );
}
